import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQuery } from 'react-query';
import queryString from 'query-string';

import ProductsAll from 'components/ProductsAll/ProductsAll';
import PageWrapper from 'components/Wrappers/PageWrapper/PageWrapper';
import ProductsNotFound from 'components/ProductsNotFound/ProductsNotFound';

import { FILTER_OPTIONS } from 'options/filterOptions';
import { PRODUCTS_MENU } from 'options/menuOptions';
import { routes } from 'options/routes';
import { PRODUCTS_PER_PAGE } from 'options/pagination';

import { addProductFilters } from 'utils/addFilters';

import { ContentWrapper } from '../Products.styles';

import { mockCategories, mockSpace } from 'components/ProductsAll/ProductsAll.data';

import { useProductOptionsQueries } from 'hooks/useProductOptionsQueries';

import { getProductSearchSite } from 'api';

const ProductsSearch = ({ userType, isStory }) => {
  const { category } = useParams();
  const [pagination, setPagination] = useState({ p: 1, l: PRODUCTS_PER_PAGE });
  const [filter, setFilter] = useState({});
  const { p } = pagination;
  const navigate = useNavigate();

  const parsedFilter = useMemo(
    () => queryString.stringify({ ...filter, ...pagination, search: category }),
    [filter, pagination, category]
  );

  const onPageChange = useCallback((_e, newPage) => {
    setPagination((prevState) => ({ ...prevState, p: newPage }));
  }, []);

  const { data, isLoading } = useQuery(['products', parsedFilter], () => getProductSearchSite(parsedFilter), {
    staleTime: 5 * 60 * 1000,
  });

  const products = data?.products || [];
  const total = data?.total || 0;
  const pageCount = useMemo(() => (total ? Math.ceil(total / PRODUCTS_PER_PAGE) : null), [total]);

  const resetPagination = useCallback(() => {
    setPagination({ p: 1, l: PRODUCTS_PER_PAGE });
  }, []);

  useEffect(() => {
    setFilter({});
    resetPagination();
  }, [category, resetPagination]);

  useEffect(() => {
    resetPagination();
  }, [filter, resetPagination]);

  const { categories, space, materials, colour } = useProductOptionsQueries();

  const itemsList = useMemo(
    () => ({
      [FILTER_OPTIONS.CATEGORY]: isStory ? mockCategories : categories,
      [FILTER_OPTIONS.SPACE]: isStory ? mockSpace : space,
      [FILTER_OPTIONS.MATERIAL]: materials || [],
      [FILTER_OPTIONS.COLOUR]: colour || [],
    }),
    [categories, colour, isStory, materials, space]
  );

  const menuOptions = useMemo(
    () =>
      PRODUCTS_MENU.filter((menuItem) => menuItem.id !== FILTER_OPTIONS.CATEGORY).map((menuItem) => {
        return {
          ...menuItem,
          actions: addProductFilters(menuItem, itemsList, setFilter, filter),
        };
      }),
    [filter, itemsList]
  );

  const title = `Results for "${category}"`;

  let notFound;

  if (!isLoading && !products?.length) {
    notFound = (
      <ProductsNotFound
        message="No products match your filter criteria."
        btnText="View All Products"
        onClick={() => navigate(routes.products)}
      />
    );
  }

  const resetFilter = useCallback(() => {
    setFilter({});
  }, []);

  const hasFilter = !!queryString.stringify(filter);

  return (
    <PageWrapper userType={userType} showSecondaryFooter={false}>
      <ContentWrapper>
        <ProductsAll
          title={title}
          userType={userType}
          menuOptions={menuOptions}
          products={products}
          isLoading={isLoading}
          notFound={notFound}
          total={total}
          page={p}
          pageCount={pageCount}
          onPageChange={onPageChange}
          resetFilter={resetFilter}
          hasFilter={hasFilter}
        />
      </ContentWrapper>
    </PageWrapper>
  );
};

ProductsSearch.propTypes = {};

export default ProductsSearch;
